"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  GraduationCap,
  Award,
  Stethoscope,
  Syringe,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookingModal } from "@/components/BookingModal";

const FOUNDERS = [
  {
    initials: "AP",
    name: "Dr. Akshay Patil",
    role: "Founder",
    subtitle: "Facial Aesthetics & Occupational Health Consultant",
    qualification: "BDS, PGDCC (Delhi), PG DOHS (Australia)",
    icon: Syringe,
    photo: "/images/dr-akshay-patil.jpg",
    accentColor: "from-amber-500/20 to-yellow-400/10",
    borderColor: "border-amber-400/40 hover:border-amber-400",
    badgeColor: "bg-amber-50 text-amber-800 border-amber-300",
    initialsColor: "bg-amber-100 text-amber-800",
    iconBg: "bg-amber-500",
    specialisations: [
      "Expert Facial Injector",
      "Botox & Neurotoxins",
      "Regenerative Medicine",
      "PRP & Biostimulators",
      "Facial Aesthetics",
      "Occupational Health",
      "Preventive Medicine",
      "Cosmetic Dermatology",
    ],
    bio: [
      "Dr. Akshay Patil is a qualified aesthetic medicine practitioner with a clinical background spanning India and Australia. A 2016 Dental Graduate, he pursued his Post Graduation in Occupational Health at Queensland University of Technology, Australia, completing it in 2019. During five years of practice in Australia, he developed deep expertise in Preventive & Regenerative Medicine  — two disciplines that continue to define his approach to patient care.",
      "Dr. Akshay is a trained and experienced facial injector, specialising in Botox and neurotoxin therapy for a wide range of aesthetic and medical indications — including facial contouring, hyperhidrosis, bruxism, TMJ disorders and brow lifting. His clinical work in regenerative medicine, including PRP, PRF, and biostimulator treatments such as Sculptra and Radiesse, reflects a commitment to evidence-based, natural-looking outcomes that work with the body rather than against it.",
      "At SkinSio, Dr. Akshay leads a patient-centred model of care where every consultation begins with listening, followed by a thorough clinical assessment and a transparent, personalised recommendation. Under his leadership, SkinSio has grown into a fully multidisciplinary centre offering skin cosmetics, laser treatments, facial aesthetics, hair restoration, physiotherapy, and dental care — all under qualified medical supervision, all under one roof.",
      "Committed to continual professional development and international best practices, Dr. Akshay regularly updates his clinical skills through advanced training in aesthetic medicine and regenerative therapies. His philosophy is simple: combine science, safety, and artistry to deliver results that are subtle, natural, and tailored to each individual's unique facial anatomy and goals.",
    ],
  },
  {
    initials: "KP",
    name: "Dr. Kanchan Patil",
    role: "Co-Founder",
    subtitle: "Chief Physiotherapist & Clinical Cosmetologist",
    qualification: "BPTh, PGDCC (UK), PGDMT, PGDND, COMT (Mumbai)",
    icon: Sparkles,
    photo: "/images/dr-kanchan-patil.jpg",
    accentColor: "from-amber-500/20 to-yellow-400/10",
    borderColor: "border-amber-400/40 hover:border-amber-400",
    badgeColor: "bg-amber-50 text-amber-800 border-amber-300",
    initialsColor: "bg-amber-100 text-amber-800",
    iconBg: "bg-amber-500",
    specialisations: [
      "Expert PMU Clinician",
      "Microblading & Lip Blushing",
      "Areola Restoration",
      "Vitiligo Camouflage",
      "Scalp Micropigmentation",
      "Clinical Cosmetology",
      "Trichology & Nutrition",
      "Physiotherapy",
      "Acne & Scar Treatments",
      "Skin Rejuvenation",
    ],
    bio: [
      "Dr. Kanchan Patil is a dual-qualified Clinical Cosmetologist and Chief Physiotherapist at SkinSio Clinics — a rare clinical combination that allows her to address both the aesthetic and physiological dimensions of patient care. Certified in Clinical Cosmetology (PGDCC, UK), Trichology, and Nutrition & Dietetics, she holds additional advanced qualifications in Myofascial Release (MFR), Instrument-Assisted Soft Tissue Mobilisation (IASTM), Cupping, Taping, and Dry Needling.",
      "Dr. Kanchan is one of Vadodara's most respected Semi-Permanent Makeup (PMU) clinicians — a field that demands not only technical mastery but an acute sense of facial proportion, symmetry, and aesthetic precision. Her PMU practice covers microblading, lip blushing, permanent eyeliner, areola restoration, Vitiligo camouflage, and scalp micropigmentation. Known for her meticulous attention to detail, perfectionist clinical standards, and steady, precise technique, she approaches every PMU procedure as an individualised, medically informed intervention rather than a routine cosmetic service. Patients consistently describe her results as natural, refined, and harmonious.",
      "Beyond PMU, her clinical cosmetology practice encompasses Medi-Facials, Skin Polishing, Acne and Scar Treatments, Wrinkle Reduction, Anti-Hairfall Protocols, Permanent Hair Reduction, and Facial Rejuvenation. Her physiotherapy training adds a further layer of clinical insight — enabling her to address the musculoskeletal and physiological factors that often underlie skin and hair concerns. At SkinSio, Dr. Kanchan is known for combining clinical precision with genuine warmth, making her one of the most trusted aesthetic practitioners in Gujarat.",
    ],
  },
];

export default function FoundersClient() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <main className="bg-background min-h-screen">

      {/* Hero */}
      <section className="relative overflow-hidden pt-28 pb-16 md:pt-32 md:pb-24">
        {/* Gold + Navy gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900" />
        {/* Decorative gold orbs */}
        <div className="absolute top-16 left-8 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute bottom-8 right-8 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-700/20 blur-3xl" />

        <div className="relative container mx-auto px-4 text-center">
          {/* Gold top rule */}
          <div className="mx-auto mb-6 h-0.5 w-16 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300" />
          <h1 className="mb-6 text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
            The Medical Minds{" "}
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              Behind SkinSio
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-blue-100/80">
            SkinSio Clinics was built on a simple belief: that patients in
            Vadodara deserve access to honest, doctor-led aesthetic and
            dermatology care — grounded in clinical evidence, not trends. Meet
            the two practitioners who made that vision a reality.
          </p>
          {/* Gold bottom rule */}
          <div className="mx-auto mt-8 h-0.5 w-16 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300" />
        </div>
      </section>

      {/* Founder Cards */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            {FOUNDERS.map((founder) => {
              const Icon = founder.icon;
              return (
                <div
                  key={founder.name}
                  className={`overflow-hidden rounded-2xl border-2 ${founder.borderColor} bg-gradient-to-br ${founder.accentColor} bg-white shadow-sm transition-all duration-300 hover:shadow-lg`}
                >
                  <div className="p-8">
                    {/* Photo + Name */}
                    <div className="mb-6 flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left">
                      <div className="relative h-32 w-32 shrink-0 overflow-hidden rounded-full ring-4 ring-white shadow-md sm:h-40 sm:w-40">
                        <Image
                          src={founder.photo}
                          alt={`${founder.name} — ${founder.subtitle}`}
                          fill
                          sizes="160px"
                          className="object-cover"
                          priority
                        />
                        <div
                          className={`absolute -right-1 -bottom-1 z-10 flex h-9 w-9 items-center justify-center rounded-full ${founder.iconBg} ring-2 ring-white sm:h-10 sm:w-10`}
                        >
                          <Icon className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                        </div>
                      </div>
                      <div>
                        <h2 className="text-foreground text-2xl font-bold">
                          {founder.name}
                        </h2>
                        <p className="mt-0.5 text-sm font-semibold text-amber-600">
                          {founder.role}
                        </p>
                        <p className="text-foreground/70 text-sm font-medium">
                          {founder.subtitle}
                        </p>
                        <div className="mt-2 flex items-start justify-center gap-1.5 sm:justify-start">
                          <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-blue-900" />
                          <p className="text-muted-foreground text-sm font-medium">
                            {founder.qualification}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Specialisations */}
                    <div className="mb-6">
                      <div className="mb-3 flex items-center gap-2">
                        <Award className="h-4 w-4 text-amber-500" />
                        <p className="text-foreground text-sm font-semibold">
                          Areas of Expertise
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {founder.specialisations.map((spec) => (
                          <span
                            key={spec}
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${founder.badgeColor}`}
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Divider with gold tint */}
                    <div className="mb-6 h-px bg-gradient-to-r from-amber-400/40 via-slate-200 to-blue-800/20" />

                    {/* Bio */}
                    <div className="space-y-4">
                      {founder.bio.map((para, i) => (
                        <p
                          key={i}
                          className="text-muted-foreground text-sm leading-relaxed"
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA — navy background with gold accents */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 to-slate-900" />
        <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl" />

        <div className="relative container mx-auto px-4 text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <Stethoscope className="h-5 w-5 text-amber-400" />
            <span className="text-sm font-semibold uppercase tracking-widest text-amber-400">
              Ready to Begin?
            </span>
          </div>
          <h2 className="mb-4 text-3xl font-bold text-white">
            Experience Doctor-Led Care at SkinSio
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-base leading-relaxed text-blue-100/80">
            Every treatment at SkinSio starts with a one-on-one clinical
            consultation — no pressure, no cookie-cutter protocols. Just honest
            medical advice tailored to your skin, your hair, and your goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="h-12 rounded-full bg-amber-500 px-8 text-white shadow-lg hover:bg-amber-400"
              onClick={() => setIsBookingOpen(true)}
            >
              Book Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link href="/services">
              <Button
                size="lg"
                className="h-12 rounded-full bg-amber-500 px-8 text-white shadow-lg hover:bg-amber-400"
              >
                View Our Services
              </Button>
            </Link>
          </div>
          <p className="mt-8 text-sm text-blue-200/60">
            A/203, The Atlantic, Makarpura Road, Near D-Mart, Vadodara, Gujarat
            – 390009
          </p>
        </div>
      </section>

      <BookingModal
        open={isBookingOpen}
        onOpenChange={setIsBookingOpen}
        onSuccess={() => setIsBookingOpen(false)}
      />
    </main>
  );
}
